/**
 * OVERVIEW — the at-a-glance answer.
 *
 * This is what `/dashboard/home` was, on correct numbers, plus the
 * highest-signal analytics panels beside it.
 *
 * The four KPI figures the home screen carried as tiles (total, peak, average,
 * active buckets) are captions and reference lines on the traffic panel rather
 * than a second strip that disagrees with the sticky one above.
 */
import React from 'react';
import { PANEL } from '../data/panel-plan';
import { PanelGrid } from '../dashboard-screen.styled';
import { useChartDrill } from '../charts/use-chart-drill';
import { useChartZoom } from '../charts/use-chart-zoom';
import { TrafficPanel } from '../panels/traffic-panel';
import { BarPanel } from '../panels/bar-panel';
import { CompositionPanel } from '../panels/composition-panel';
import { SeverityPanel } from '../panels/severity-panel';
import { AllFiguresPanel } from '../panels/all-figures-panel';
import { QualityHeadline } from '../panels/quality-headline';
import { ShelfPanel } from '../panels/shelf-panel';
import { ActivityPanel } from '../panels/activity-panel';
import { partialDayEndsAt } from '../charts/chart-theme';
import { TabContext, useResults } from './trends-tab';

export const OverviewTab: React.FC<TabContext> = ({
  filter,
  filterMeta,
  data,
  dispatch,
  isMobile,
  onExportPanelPng,
  onExportPanelCsv,
}) => {
  const results = useResults(data);
  const zoom = useChartZoom({ filter, dispatch });

  const measure = filterMeta?.measures.find(m => m.key === filter.measure);
  const measureLabel = measure?.label || filter.measure;
  const dimensions = measure?.dimensions || [];
  const ready = Boolean(filterMeta);
  const has = (dimension: string) => !ready || dimensions.includes(dimension);

  const batchState = {
    loading: data.batch.loading,
    stale: data.batch.stale,
    error: data.batch.error,
    onRetry: data.batch.retry,
  };

  const timeDrill = useChartDrill({
    panelId: 'traffic',
    dimension: filter.granularity === 'week' ? 'week' : 'day',
    filter,
    dispatch,
  });
  const buildingDrill = useChartDrill({
    panelId: 'league',
    dimension: 'building',
    filter,
    dispatch,
  });
  const kindDrill = useChartDrill({
    panelId: 'composition',
    dimension: 'kind',
    filter,
    dispatch,
  });
  const severityDrill = useChartDrill({
    panelId: 'severity',
    dimension: 'severity',
    filter,
    dispatch,
  });

  const window = data.batch.data?.window || data.overview.data?.window;

  return (
    <PanelGrid data-testid="panel-grid">
      <TrafficPanel
        state={batchState}
        result={results[PANEL.pulseEntries]}
        granularity={filter.granularity}
        comparing={filter.compare === 'previous_period'}
        endsAt={partialDayEndsAt(window?.to, window?.tz || 'UTC')}
        seriesOff={filter.seriesOff}
        onToggleSeries={key =>
          dispatch({ type: 'toggleMulti', field: 'seriesOff', value: `traffic:${key}` })
        }
        drill={timeDrill}
        zoom={zoom}
        showBrush={!isMobile}
        onExportPng={svg => onExportPanelPng('traffic', svg)}
        onExportCsv={() => onExportPanelCsv(PANEL.pulseEntries)}
      />

      {has('building') ? (
        <BarPanel
          id="league"
          title="Building league"
          wide
          state={batchState}
          result={results[PANEL.league]}
          caveat={`${measureLabel} per building, ranked. A building that recorded nothing still has a row — that is the most useful row on this panel. Choose one to scope the whole page to it.`}
          emptyTitle="No buildings in scope"
          emptyText="Nothing you can see belongs to this selection, so there is nothing to rank."
          onSelect={item => buildingDrill.onValue(item.key, item.label)}
          selectVerb="Scope the page to"
          onExportCsv={() => onExportPanelCsv(PANEL.league)}
        />
      ) : null}

      {has('kind') ? (
        <CompositionPanel
          state={batchState}
          result={results[PANEL.composition]}
          isMobile={isMobile}
          drill={kindDrill}
          onExportPng={svg => onExportPanelPng('composition', svg)}
          onExportCsv={() => onExportPanelCsv(PANEL.composition)}
        />
      ) : null}

      {has('severity') ? (
        <SeverityPanel
          state={batchState}
          result={results[PANEL.severity]}
          drill={severityDrill}
          onExportPng={svg => onExportPanelPng('severity', svg)}
          onExportCsv={() => onExportPanelCsv(PANEL.severity)}
        />
      ) : null}

      {/* THE SHELF AND THE THRESHOLD, as two rings. The only two figures on this
          screen that are a part of a KNOWN whole, which is the one case a ring
          beats a bar. */}
      <ShelfPanel
        state={{
          loading: data.overview.loading,
          stale: data.overview.stale,
          error: data.overview.error,
          onRetry: data.overview.retry,
        }}
        overview={data.overview.data}
        breached={data.latency.data?.breached ?? null}
        breachedOf={data.latency.data?.count ?? null}
        thresholdHours={data.latency.data?.thresholdHours ?? null}
        onExportCsv={() => onExportPanelCsv('shelf')}
      />

      {/* THE ONE PANEL THAT CAN SAY THE REST OF THE SCREEN IS UNRELIABLE.
          Two of its six ratios are the headline; the full set is on Trends. */}
      <QualityHeadline
        state={{
          loading: data.quality.loading,
          stale: data.quality.stale,
          error: data.quality.error,
          onRetry: data.quality.retry,
        }}
        quality={data.quality.data}
        latency={data.latency.data}
        latencyLoading={data.latency.loading}
        onSeeAll={() => dispatch({ type: 'setTab', tab: 'trends' })}
      />

      {/* The whole activity record, condensed. Same component and same endpoint
          as the Activity tab, with the filters collapsed rather than removed. */}
      <ActivityPanel
        state={{
          loading: data.activity.loading,
          stale: data.activity.stale,
          error: data.activity.error,
          onRetry: data.activity.retry,
        }}
        data={data.activity.data}
        filter={filter}
        dispatch={dispatch}
        compact
        onSeeAll={() => dispatch({ type: 'setTab', tab: 'activity' })}
      />

      <AllFiguresPanel
        state={{
          loading: data.overview.loading,
          stale: data.overview.stale,
          error: data.overview.error,
          onRetry: data.overview.retry,
        }}
        overview={data.overview.data}
        onExportCsv={() => onExportPanelCsv('all-figures')}
      />
    </PanelGrid>
  );
};
